import { cardBoxShadow, cardTransition, Colors, FOCUS_RING } from '@amelio/ui-library';
import { useState } from 'react';

import type { AnswerOptionProps } from './types';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';
import { KeyboardFocus } from '@/constants/KeyboardFocus';

const SELECTED_ARIA_SUFFIX = ', selected';

/**
 * AnswerOption component - Individual option item in an AnswerOptionsList
 *
 * Displays a single selectable option with:
 * - Semi-transparent background on dark surfaces (bg-white/10)
 * - A visible indicator ring on every option, filled with a solid dot when selected
 * - Selected card gets the primary border plus the shared cardBoxShadow halo
 * - Temporary glow on selection via .glow-once CSS class
 * - Click handler for selection
 */
export const AnswerOption = ({
    label,
    value,
    isSelected,
    disabled = false,
    onClick,
    children,
    dataTestId,
    isRtl = false,
}: AnswerOptionProps) => {
    const [isGlowing, setIsGlowing] = useState(false);

    const handleClick = () => {
        if (disabled) {
            return;
        }
        if (!isSelected) {
            setIsGlowing(true);
        }
        onClick(value);
    };

    const handleAnimationEnd = () => {
        setIsGlowing(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(value);
        }
    };

    const baseClasses = [
        'w-full',
        'ds-p-lg md:ds-p-2xl',
        'rounded-xl',
        'border-2',
        'text-start',
        cardTransition,
        'flex',
        'items-center',
        'gap-3',
        'group',
        'relative',
        FOCUS_RING,
    ].join(' ');

    // A fainter border on an unselected option measures ~1.2:1 against the survey background
    // and fails WCAG 1.4.11's 3:1 minimum for UI-component contrast. border-white/40 keeps the
    // same subtle look while measuring ~3.8:1.
    const stateClasses = isSelected
        ? 'bg-white/20 text-white'
        : 'bg-white/10 border-white/40 text-white hover:bg-white/20 hover:border-white/60';

    // A selected card carries a soft glow around its border; cardBoxShadow is the same halo
    // AnswerEmojiItem already uses, so both answer layouts mark selection identically.
    const selectedBorderStyle = isSelected
        ? { borderColor: Colors.PRIMARY, boxShadow: cardBoxShadow }
        : {};

    const disabledClasses = disabled ? 'cursor-not-allowed opacity-50' : '';
    const glowClass = isGlowing && !disabled ? 'glow-once' : '';

    const buttonClasses = [baseClasses, stateClasses, disabledClasses, glowClass]
        .filter(Boolean)
        .join(' ');

    return (
        <button
            type={'button'}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onAnimationEnd={handleAnimationEnd}
            disabled={disabled}
            tabIndex={disabled ? KeyboardFocus.DISABLED : KeyboardFocus.FOCUSABLE}
            className={buttonClasses}
            style={selectedBorderStyle}
            dir={isRtl ? 'rtl' : 'ltr'}
            aria-pressed={isSelected}
            aria-label={`${label}${isSelected ? SELECTED_ARIA_SUFFIX : ''}`}
            data-testid={dataTestId}
            data-selected={isSelected}
        >
            {/* Fixed-size slot at the reading-start edge (mirrors with `dir`), always
                rendered so selecting an option doesn't shift the label's position. The
                unselected state keeps a visible ring rather than a blank space, and the
                selected state is marked with a plain dot rather than a checkmark glyph. */}
            <div
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                    isSelected ? '' : 'border border-white/40'
                }`}
                style={isSelected ? { backgroundColor: Colors.PRIMARY } : undefined}
            >
                {isSelected && (
                    <div
                        className={'h-3 w-3 rounded-full bg-white'}
                        data-testid={'selected-option-dot'}
                    />
                )}
            </div>

            {children || (
                <span className={'text-md font-normal'}>
                    <QuestionTextRenderer text={label} />
                </span>
            )}
        </button>
    );
};
